/**
 * Upload Workspace Standard Dialog
 *
 * Modal for uploading a new workspace-level standard document.
 * Accepts PDF, DOCX, DOC, TXT files with standard selection and optional description.
 */

import { useState, useCallback, useRef, useMemo } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { Upload, FileText, X, Loader2, Search, Check } from "lucide-react";
import { Button, Input, Textarea } from "@/components/ui/primitives";
import { useApiMutation } from "@/packages/features-shared";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import { COMMON_STANDARDS, type CommonStandard } from "@/packages/standards";

const ACCEPTED_EXTENSIONS = [".pdf", ".docx", ".doc", ".txt"];

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

type StandardSelection =
  | { type: "known"; standard: CommonStandard }
  | { type: "custom"; name: string; code: string }
  | null;

interface UploadWorkspaceStandardDialogProps {
  open: boolean;
  onClose: () => void;
}

export function UploadWorkspaceStandardDialog({
  open,
  onClose,
}: UploadWorkspaceStandardDialogProps) {
  const { t } = useTranslation("common");
  const workspaceId = useWorkspaceId();

  const generateUploadUrl = useApiMutation(
    api.workspace_standards_mutations.generateUploadUrl,
    { name: "generate-upload-url", silentSuccess: true },
  );
  const uploadMutation = useApiMutation(
    api.workspace_standards_mutations.upload,
    {
      name: "upload-workspace-standard",
      successMessage: t("workspaceStandards.upload.successMessage"),
    },
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selection, setSelection] = useState<StandardSelection>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [description, setDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredStandards = useMemo(() => {
    if (!searchQuery.trim()) return COMMON_STANDARDS;
    const q = searchQuery.toLowerCase();
    return COMMON_STANDARDS.filter(
      (s) =>
        s.displayName.toLowerCase().includes(q) ||
        s.code.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  const showOtherOnly =
    searchQuery.trim().length > 0 && filteredStandards.length === 0;

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setSelection(null);
    setSearchQuery("");
    setIsComboboxOpen(false);
    setCustomName("");
    setCustomCode("");
    setDescription("");
    setIsUploading(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    resetForm();
    onClose();
  }, [isUploading, resetForm, onClose]);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!isAcceptedFile(file)) {
        setError(t("workspaceStandards.upload.errors.invalidFileType"));
        return;
      }
      setError(null);
      setSelectedFile(file);
    },
    [t],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleSelectStandard = useCallback((standard: CommonStandard) => {
    setSelection({ type: "known", standard });
    setSearchQuery(standard.displayName);
    setIsComboboxOpen(false);
  }, []);

  const handleSelectOther = useCallback(() => {
    const query = searchQuery.trim();
    setSelection({ type: "custom", name: "", code: "" });
    setCustomName(query);
    setCustomCode(
      query
        .toUpperCase()
        .replace(/[^A-Z0-9_]/g, "_")
        .slice(0, 10),
    );
    setIsComboboxOpen(false);
  }, [searchQuery]);

  const handleClearSelection = useCallback(() => {
    setSelection(null);
    setSearchQuery("");
    setCustomName("");
    setCustomCode("");
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const getSubmitValues = useCallback((): {
    name: string;
    code: string;
  } | null => {
    if (!selection) return null;
    if (selection.type === "known") {
      return {
        name: selection.standard.displayName,
        code: selection.standard.code,
      };
    }
    const trimmedName = customName.trim();
    const trimmedCode = customCode.trim();
    if (!trimmedName || !trimmedCode) return null;
    return { name: trimmedName, code: trimmedCode };
  }, [selection, customName, customCode]);

  const handleSubmit = useCallback(async () => {
    const values = getSubmitValues();
    if (!selectedFile || !values) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploadUrl = await generateUploadUrl.mutateAsync({});

      const response = await fetch(uploadUrl, {
        method: "POST",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type || "application/octet-stream",
        },
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const { storageId } = (await response.json()) as {
        storageId: Id<"_storage">;
      };

      await uploadMutation.mutateAsync({
        workspaceId,
        documentStorageId: storageId,
        fileName: values.name,
        name: values.name,
        code: values.code,
        description: description.trim() || undefined,
      });

      resetForm();
      onClose();
    } catch {
      setError(t("workspaceStandards.upload.errors.uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  }, [
    getSubmitValues,
    selectedFile,
    description,
    generateUploadUrl,
    uploadMutation,
    workspaceId,
    resetForm,
    onClose,
    t,
  ]);

  const isFormValid = selectedFile !== null && getSubmitValues() !== null;

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="w-full max-w-[480px] rounded-[12px] bg-card shadow-xl"
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{
                    type: "spring",
                    damping: 25,
                    stiffness: 300,
                  }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <Dialog.Title className="text-[15px] font-semibold text-primary">
                      {t("workspaceStandards.upload.title")}
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="p-1 rounded-md hover:bg-card-hover transition-colors"
                        aria-label="Close dialog"
                      >
                        <X size={16} className="text-muted" />
                      </button>
                    </Dialog.Close>
                  </div>

                  {/* Body */}
                  <div className="p-5 space-y-4">
                    {/* File drop zone */}
                    <div>
                      <label
                        htmlFor="ws-standard-document-input"
                        className="block text-[13px] font-medium text-secondary mb-1.5"
                      >
                        {t("workspaceStandards.upload.standardDocument")}
                      </label>

                      {selectedFile ? (
                        <div className="flex items-center gap-3 rounded-lg border border-border bg-card-hover/30 px-4 py-3">
                          <div className="rounded-md p-1.5 bg-accent/10 shrink-0">
                            <FileText size={16} className="text-accent" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-primary truncate">
                              {selectedFile.name}
                            </p>
                            <p className="text-[11px] text-muted">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          {!isUploading && (
                            <button
                              type="button"
                              onClick={() => setSelectedFile(null)}
                              className="p-1 rounded-md hover:bg-card-hover transition-colors"
                              aria-label="Remove file"
                            >
                              <X size={14} className="text-muted" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          disabled={isUploading}
                          className="w-full rounded-lg border-2 border-dashed border-border hover:border-accent/50 bg-card-hover/20 px-4 py-8 text-center transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Upload
                            size={24}
                            className="mx-auto mb-2 text-muted/60"
                          />
                          <span className="block text-[13px] font-medium text-secondary mb-1">
                            {t("workspaceStandards.upload.dropZoneText")}
                          </span>
                          <span className="block text-[11px] text-muted">
                            {t("workspaceStandards.upload.dropZoneSubtext")}
                          </span>
                        </button>
                      )}

                      <input
                        ref={fileInputRef}
                        id="ws-standard-document-input"
                        type="file"
                        accept=".pdf,.docx,.doc,.txt"
                        onChange={handleFileInputChange}
                        className="hidden"
                        tabIndex={-1}
                      />
                    </div>

                    {/* Standard selector combobox */}
                    <div>
                      <span className="block text-[13px] font-medium text-secondary mb-1.5">
                        {t("workspaceStandards.upload.standard")}
                      </span>

                      {selection?.type === "known" ? (
                        /* Selected known standard chip */
                        <div className="flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5">
                          <Check size={14} className="text-accent shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-primary">
                              {selection.standard.displayName}
                            </p>
                            <p className="text-[11px] text-muted">
                              {selection.standard.code} &middot;{" "}
                              {selection.standard.description}
                            </p>
                          </div>
                          {!isUploading && (
                            <button
                              type="button"
                              onClick={handleClearSelection}
                              className="p-1 rounded-md hover:bg-card-hover transition-colors"
                              aria-label={t(
                                "workspaceStandards.upload.changeStandard",
                              )}
                            >
                              <X size={14} className="text-muted" />
                            </button>
                          )}
                        </div>
                      ) : selection?.type === "custom" ? (
                        /* Custom standard inputs */
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-[12px] text-muted">
                              {t("workspaceStandards.upload.customStandard")}
                            </span>
                            {!isUploading && (
                              <button
                                type="button"
                                onClick={handleClearSelection}
                                className="text-[12px] text-accent hover:underline"
                              >
                                {t("workspaceStandards.upload.changeStandard")}
                              </button>
                            )}
                          </div>
                          <div>
                            <label
                              htmlFor="ws-standard-custom-name"
                              className="block text-[12px] text-muted mb-1"
                            >
                              {t("workspaceStandards.upload.name")}
                            </label>
                            <Input
                              id="ws-standard-custom-name"
                              value={customName}
                              onChange={(e) => setCustomName(e.target.value)}
                              placeholder={t(
                                "workspaceStandards.upload.namePlaceholder",
                              )}
                              disabled={isUploading}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="ws-standard-custom-code"
                              className="block text-[12px] text-muted mb-1"
                            >
                              {t("workspaceStandards.upload.code")}
                            </label>
                            <Input
                              id="ws-standard-custom-code"
                              value={customCode}
                              onChange={(e) =>
                                setCustomCode(
                                  e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9_]/g, ""),
                                )
                              }
                              placeholder={t(
                                "workspaceStandards.upload.codePlaceholder",
                              )}
                              disabled={isUploading}
                              maxLength={10}
                            />
                            <p className="text-[11px] text-muted mt-1">
                              {t("workspaceStandards.upload.codeHint")}
                            </p>
                          </div>
                        </div>
                      ) : (
                        /* Combobox search */
                        <div className="relative" ref={comboboxRef}>
                          <div className="relative">
                            <Search
                              size={14}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
                            />
                            <input
                              ref={inputRef}
                              type="text"
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setIsComboboxOpen(true);
                              }}
                              onFocus={() => setIsComboboxOpen(true)}
                              onBlur={(e) => {
                                // Keep open if clicking inside the dropdown
                                if (
                                  comboboxRef.current?.contains(
                                    e.relatedTarget as Node,
                                  )
                                )
                                  return;
                                setTimeout(() => setIsComboboxOpen(false), 150);
                              }}
                              placeholder={t(
                                "workspaceStandards.upload.searchStandards",
                              )}
                              disabled={isUploading}
                              className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-[13px] text-primary placeholder:text-muted/60 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-colors disabled:opacity-50"
                            />
                          </div>

                          {/* Dropdown */}
                          <AnimatePresence>
                            {isComboboxOpen && (
                              <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.15 }}
                                className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-[240px] overflow-y-auto"
                              >
                                {!showOtherOnly &&
                                  filteredStandards.map((standard) => (
                                    <button
                                      key={standard.code}
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() =>
                                        handleSelectStandard(standard)
                                      }
                                      className="w-full text-left px-3 py-2 hover:bg-card-hover transition-colors first:rounded-t-lg"
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-[13px] font-medium text-primary truncate">
                                          {standard.displayName}
                                        </span>
                                        <span className="text-[11px] text-muted shrink-0 font-mono">
                                          {standard.code}
                                        </span>
                                      </div>
                                      <p className="text-[11px] text-muted mt-0.5 truncate">
                                        {standard.description}
                                      </p>
                                    </button>
                                  ))}

                                {/* Separator + Other */}
                                {!showOtherOnly &&
                                  filteredStandards.length > 0 && (
                                    <div className="border-t border-border" />
                                  )}
                                <button
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={handleSelectOther}
                                  className="w-full text-left px-3 py-2.5 hover:bg-card-hover transition-colors rounded-b-lg"
                                >
                                  <span className="text-[13px] font-medium text-secondary">
                                    {t("workspaceStandards.upload.otherCustom")}
                                  </span>
                                  <p className="text-[11px] text-muted mt-0.5">
                                    {t(
                                      "workspaceStandards.upload.otherCustomHint",
                                    )}
                                  </p>
                                </button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label
                        htmlFor="ws-standard-description"
                        className="block text-[13px] font-medium text-secondary mb-1.5"
                      >
                        {t("workspaceStandards.upload.description")}
                      </label>
                      <Textarea
                        id="ws-standard-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t(
                          "workspaceStandards.upload.descriptionPlaceholder",
                        )}
                        disabled={isUploading}
                        rows={3}
                      />
                    </div>

                    {/* Error message */}
                    {error && (
                      <p className="text-[12px] text-red-500">{error}</p>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
                    <Button
                      variant="secondary"
                      onClick={handleClose}
                      disabled={isUploading}
                    >
                      {t("actions.cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isUploading}
                      loading={isUploading}
                      icon={isUploading ? Loader2 : Upload}
                    >
                      {isUploading
                        ? t("workspaceStandards.upload.uploading")
                        : t("workspaceStandards.upload.uploadButton")}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
